import { Component, ReactNode } from "react";
import { Router } from "../../lib/routes";
import styled from "styled-components";
import { Formik, Form, FormikActions, FormikErrors, FormikProps } from "formik";
import * as yup from "yup";
import Button from "../common/Button";
import LoadingIndicator from "../common/LoadingIndicator";
import ScrollIntoView from "../common/ScrollIntoView";
import ErrorMarker, { ScrollToError } from "./input/ErrorMarker";
import Message from "./Message";
import { FlexRow } from "../common/Flex";
import * as React from "react";

const Loading = styled(LoadingIndicator)``;

type Config<T> = {
  initialValues?: T;
  fieldSchema?: { [P in keyof T]?: yup.Schema<T[P]> };
  validate?: (
    values: T,
    errors: FormikErrors<T>
  ) => void | FormikErrors<T> | Promise<FormikErrors<T>>;
  submitLabel?: string;
  handleSubmit: (values: T, submitProps: any) => any;
  handleReset?: () => void;
  handleError?: (error: any, formik: FormikActions<T>) => any;
  renderSuccess?: (result: any) => ReactNode;
  renderSuccessTop?: (result: any) => ReactNode;
  renderError?: (error: any) => ReactNode;
  noValidate?: boolean;
};

export type Props<Values> = {
  defaultValues?: Partial<Values>;
  submitLabel?: string;
  resetLabel?: string;
  onSuccess?: (result: any) => any;
  children: ((props: FormikProps<Values>) => React.ReactNode) | React.ReactNode;
  submitProps?: any;
};

function defaultErrorRenderer(error: any) {
  if (typeof error == "string") return error;
  if ("message" in error) return error.message;
  return "Bei der Verarbeitung Ihrer Daten ist ein Fehler aufgetreten.";
}

export default function buildForm<Values>(config: Config<Values>) {
  return (props: Props<Values>) => (
    <FormReloadWrapper config={config} props={props} />
  );
}
type State = {
  reload: boolean;
};
class FormReloadWrapper<Values> extends Component<
  {
    config: Config<Values>;
    props: Props<Values>;
  },
  State
> {
  state = {
    reload: false
  };
  componentDidMount() {
    Router.events.on("routeChangeStart", this.onRouteChangeStart);
  }
  onRouteChangeStart = () => {
    this.setState(prev => ({ reload: !prev.reload }));
  };

  componentWillUnmount() {
    Router.events.off("routeChangeStart", this.onRouteChangeStart);
  }
  render() {
    const {
      initialValues,
      fieldSchema,
      validate,
      handleSubmit,
      handleError,
      renderError = defaultErrorRenderer,
      renderSuccess,
      renderSuccessTop,
      handleReset,
      noValidate
    } = this.props.config;
    const {
      defaultValues,
      submitProps,
      submitLabel = "Absenden",
      resetLabel = "Zurücksetzen",
      onSuccess,
      children
    } = this.props.props;

    const validationSchema = fieldSchema && yup.object().shape(fieldSchema);

    return (
      <Formik
        key={"form" + this.state.reload}
        initialValues={{ ...initialValues, ...defaultValues }}
        validationSchema={validationSchema}
        validate={values => {
          if (!validate) return;
          // Call custom validation function with a strictly typed errors object
          const errors: FormikErrors<Values> = {};
          return validate(values, errors) || errors;
        }}
        onSubmit={async (values, formik) => {
          try {
            const result = await handleSubmit(values, submitProps);
            if (onSuccess) await onSuccess(result);
            formik.resetForm({ ...initialValues, ...defaultValues, ...values });
            formik.setStatus({ result, success: true });
          } catch (error) {
            if (handleError) error = handleError(error, formik);
            formik.setStatus({ error });
          }
          formik.setSubmitting(false);
        }}
        render={props => {
          const { status = {}, isSubmitting } = props;
          const { success, error, result } = status;
          if (success && renderSuccess) {
            return <ScrollIntoView>{renderSuccess(result)}</ScrollIntoView>;
          }
          return (
            <Form noValidate={noValidate}>
              {error && (
                <>
                  <ErrorMarker />
                  <Message red>{renderError(error)}</Message>
                </>
              )}
              {success && renderSuccessTop && (
                <>
                  <ErrorMarker />
                  <Message>{renderSuccessTop(result)}</Message>
                </>
              )}
              {typeof children === "function" ? children(props) : children}
              <FlexRow
                justifyContent="space-between"
                alignItems="center"
                flexWrap
              >
                <Button
                  type="reset"
                  ghost
                  onClick={() => {
                    props.resetForm({ ...initialValues, ...defaultValues });
                    handleReset && handleReset();
                  }}
                >
                  {resetLabel}
                </Button>
                {isSubmitting ? (
                  <Loading />
                ) : (
                  <Button type="submit">{submitLabel}</Button>
                )}
              </FlexRow>
              {isSubmitting && <ScrollToError />}
            </Form>
          );
        }}
      />
    );
  }
}
